import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function ComicCard({ comic }: { comic: Comic }) {
  const [imgError, setImgError] = useState(false);
  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block"
    >
      <div className="relative overflow-hidden rounded-lg bg-background-100">
        <div className="aspect-[3/4] w-full overflow-hidden">
          {imgError ? (
            <div className="w-full h-full flex items-center justify-center bg-background-200 text-foreground-400">
              <div className="w-12 h-12 flex items-center justify-center">
                <i className="ri-image-line text-3xl"></i>
              </div>
            </div>
          ) : (
            <img
              src={comic.coverImage}
              alt={`${comic.title} - ${comic.category}韩国漫画`}
              title={`${comic.title} - 免费${comic.category}韩国漫画在线阅读`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              onError={() => setImgError(true)}
            />
          )}
        </div>
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${
            comic.status === '连载中'
              ? 'bg-primary-500 text-background-50'
              : 'bg-secondary-500 text-background-50'
          }`}>
            {comic.status}
          </span>
        </div>
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-background-950/70 text-background-50">
            {comic.rating}分
          </span>
        </div>
      </div>
      <div className="mt-3 px-0.5">
        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
          {comic.title}
        </h3>
        <p className="mt-1 text-xs text-foreground-500">{comic.author}</p>
        <div className="mt-1.5 flex items-center gap-3">
          <span className="text-xs text-primary-600 font-medium">
            <span className="inline-flex items-center gap-0.5">
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-eye-line text-xs"></i>
              </div>
              {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}
            </span>
          </span>
          <span className="text-xs text-foreground-400">{comic.category}</span>
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { categoryName = 'all' } = useParams();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'rating'>('popular');
  const [filteredComics, setFilteredComics] = useState<Comic[]>([]);

  const currentCategory = categories.find((c) => c.slug === categoryName) || categories[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [categoryName, sortBy]);

  useEffect(() => {
    let result = categoryName === 'all'
      ? [...comics]
      : comics.filter((c) => c.categorySlug === categoryName);

    if (sortBy === 'popular') {
      result.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'newest') {
      result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    }

    setFilteredComics(result);
  }, [categoryName, sortBy]);

  const handleCategoryChange = (slug: string) => {
    navigate(`/category/${slug}`);
  };

  const categoryPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': `${currentCategory.name}韩国漫画 - 韩漫之家`,
    'description': `韩漫之家${currentCategory.name}漫画分类，为您提供最全的高清${currentCategory.name}韩国漫画下拉式在线阅读资源。`,
    'url': `https://www.manhuazhijia.com/category/${categoryName}`,
    'isPartOf': {
      '@type': 'WebSite',
      'name': '韩漫之家',
      'url': 'https://www.manhuazhijia.com/',
    },
    'breadcrumb': {
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': '首页',
          'item': 'https://www.manhuazhijia.com/',
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': `${currentCategory.name}漫画`,
          'item': `https://www.manhuazhijia.com/category/${categoryName}`,
        },
      ],
    },
  };

  const pageTitle = categoryName === 'all'
    ? '全部韩漫-韩国漫画分类浏览-韩漫之家'
    : `${currentCategory.name}韩漫-${currentCategory.name}韩国漫画下拉式在线阅读-韩漫之家`;

  return (
    <>
      <head>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={`韩漫之家${currentCategory.name}漫画分类，为您提供海量高清${currentCategory.name}韩国漫画资源，下拉式在线阅读体验，畅享${currentCategory.name}漫画的精彩世界。`}
        />
        <meta name="keywords" content={`${currentCategory.name}韩漫,${currentCategory.name}韩国漫画,下拉式在线阅读,高清漫画大全,韩漫之家`} />
        <link rel="canonical" href={`https://www.manhuazhijia.com/category/${categoryName}`} />
        <script type="application/ld+json">
          {JSON.stringify(categoryPageSchema)}
        </script>
      </head>

      <main className="pt-16">
        <div className="bg-gradient-to-b from-primary-500/10 to-background-50 py-8 md:py-12">
          <div className="px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-1.5 text-sm mb-4" aria-label="面包屑导航">
                <Link to="/" className="text-foreground-500 hover:text-primary-600 transition-colors">首页</Link>
                <span className="text-foreground-300">/</span>
                <span className="text-foreground-900 font-medium">{currentCategory.name}漫画</span>
              </nav>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {currentCategory.name}漫画
              </h1>
              <p className="mt-2 text-sm text-foreground-500">
                共 {filteredComics.length} 部漫画作品
              </p>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2 flex-wrap" role="tablist" aria-label="漫画分类">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    role="tab"
                    aria-selected={categoryName === cat.slug}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      categoryName === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 bg-background-100 rounded-lg p-1">
                {([
                  { value: 'popular', label: '最热' },
                  { value: 'newest', label: '最新' },
                  { value: 'rating', label: '评分' },
                ] as const).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === opt.value
                        ? 'bg-background-50 text-foreground-900'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                  <i className="ri-file-search-line text-2xl"></i>
                </div>
                <p className="mt-4 text-foreground-500">暂无该分类的漫画，敬请期待</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}